import React, { useState } from 'react';
import { defaultTableBlock, COLUMN_STYLES, COLUMN_ALIGNS, slugifyColumnKey } from './shared';
import { DragHandle, SortableItem, reorder } from '../pageEditor/sortable';
import OptionGroup from '../pageEditor/OptionGroup';
import AdminModal from '../../../components/admin/AdminModal';

function uniqueKey(columns, base) {
  const taken = new Set(columns.map((c) => c.key));
  let key = base;
  for (let n = 2; taken.has(key); n += 1) key = `${base}_${n}`;
  return key;
}

function parsePastedRows(text, columns) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim())
    .map((line) => {
      const cells = line.includes('\t') ? line.split('\t') : line.split(/\s*\|\s*/);
      const row = {};
      columns.forEach((col, i) => { row[col.key] = (cells[i] ?? '').trim(); });
      return row;
    });
}

function TableEditor({ table, tIdx, onChange, onDuplicate, onRemove }) {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [expanded, setExpanded] = useState(false);
  const columns = table.columns || [];
  const rows = table.rows || [];
  const update = (patch) => onChange({ ...table, ...patch });

  const updateColumn = (colIdx, patch) => {
    const next = [...columns];
    const prevKey = next[colIdx]?.key;
    next[colIdx] = { ...next[colIdx], ...patch };
    if (patch.key !== undefined && patch.key !== prevKey) {
      update({
        columns: next,
        rows: rows.map((row) => {
          const { [prevKey]: value, ...rest } = row;
          return { ...rest, [patch.key]: value ?? '' };
        }),
      });
      return;
    }
    update({ columns: next });
  };

  const addColumn = () => {
    const key = uniqueKey(columns, `col${columns.length + 1}`);
    update({
      columns: [...columns, { key, label: 'Column', style: 'text' }],
      rows: rows.map((row) => ({ ...row, [key]: '' })),
    });
  };

  const removeColumn = (colIdx) => {
    const key = columns[colIdx]?.key;
    update({
      columns: columns.filter((_, i) => i !== colIdx),
      rows: rows.map((row) => {
        const { [key]: _removed, ...rest } = row;
        return rest;
      }),
    });
  };

  const emptyRow = () => Object.fromEntries(columns.map((col) => [col.key, col.style === 'permission' ? 'EVERYONE' : '']));

  const setRow = (rowIdx, patch) => {
    const next = [...rows];
    next[rowIdx] = { ...next[rowIdx], ...patch };
    update({ rows: next });
  };

  const applyPaste = () => {
    const parsed = parsePastedRows(pasteText, columns);
    if (parsed.length) update({ rows: [...rows, ...parsed] });
    setPasteText('');
    setPasteOpen(false);
  };

  const renderGrid = () => (
    <div className="tpb-grid-editor-shell">
      <div className="tpb-grid-editor-wrap">
        <table className="tpb-grid-editor">
          <thead>
            <tr>
              <th aria-label="Drag" />
              {columns.map((col) => <th key={col.key}>{col.label || col.key}</th>)}
              <th aria-label="Row actions" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + 2} className="tpb-grid-editor__empty">No rows yet. Add one or paste from a spreadsheet.</td></tr>
            )}
            {rows.map((row, rIdx) => (
              <SortableItem
                as="tr"
                key={rIdx}
                className={row._highlight ? 'is-highlighted' : ''}
                listId={`table-rows-${tIdx}`}
                index={rIdx}
                onMove={(from, to) => update({ rows: reorder(rows, from, to) })}
              >
                <td className="tpb-grid-editor__handle"><DragHandle listId={`table-rows-${tIdx}`} index={rIdx} /></td>
                {columns.map((col) => (
                  <td key={col.key}>
                    <input
                      value={row[col.key] ?? ''}
                      onChange={(e) => setRow(rIdx, { [col.key]: e.target.value })}
                      placeholder={col.style === 'link' ? 'Label | https://…' : ''}
                      aria-label={`${col.label || col.key} row ${rIdx + 1}`}
                    />
                  </td>
                ))}
                <td className="tpb-grid-editor__actions">
                  <button type="button" className={`admin-icon-btn ${row._highlight ? 'is-active' : ''}`} title="Highlight row" onClick={() => setRow(rIdx, { _highlight: !row._highlight || undefined })}>
                    <i className="fas fa-highlighter" aria-hidden="true" />
                  </button>
                  <button type="button" className="admin-icon-btn" title="Duplicate row" onClick={() => update({ rows: [...rows.slice(0, rIdx + 1), { ...row }, ...rows.slice(rIdx + 1)] })}>
                    <i className="far fa-copy" aria-hidden="true" />
                  </button>
                  <button type="button" className="admin-icon-btn danger" title="Delete row" onClick={() => update({ rows: rows.filter((_, i) => i !== rIdx) })}>
                    <i className="fas fa-trash" aria-hidden="true" />
                  </button>
                </td>
              </SortableItem>
            ))}
          </tbody>
        </table>
      </div>
      <div className="tpb-grid-editor__toolbar">
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => update({ rows: [...rows, emptyRow()] })}>
          <i className="fas fa-plus" aria-hidden="true" /> Add row
        </button>
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setPasteOpen((v) => !v)}>
          <i className="fas fa-paste" aria-hidden="true" /> Paste rows
        </button>
        {!expanded && (
          <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setExpanded(true)}>
            <i className="fas fa-expand" aria-hidden="true" /> Expand
          </button>
        )}
      </div>
      {pasteOpen && (
        <div className="tpb-paste-box">
          <textarea
            rows={5}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={`One row per line. Copy cells from Excel / Google Sheets, or separate with |\nColumns in order: ${columns.map((c) => c.label || c.key).join(' | ')}`}
          />
          <button type="button" className="btn btn-primary tpb-add-btn" onClick={applyPaste} disabled={!pasteText.trim()}>
            Add {parsePastedRows(pasteText, columns).length} rows
          </button>
        </div>
      )}
    </div>
  );

  const title = [table.heading, table.highlight].filter(Boolean).join(' ') || `Table ${tIdx + 1}`;

  return (
    <>
      <div className="stream-table-editor-block__head">
        <DragHandle listId="custom-tables" index={tIdx} />
        <strong>{title}</strong>
        <span className="admin-pill is-muted">{rows.length} {rows.length === 1 ? 'row' : 'rows'}</span>
        <div className="stream-table-editor-block__actions">
          <button type="button" className="admin-icon-btn" title="Duplicate table" onClick={onDuplicate}>
            <i className="far fa-copy" aria-hidden="true" />
          </button>
          <button type="button" className="admin-icon-btn danger" title="Remove table" onClick={onRemove}>
            <i className="fas fa-trash" aria-hidden="true" />
          </button>
        </div>
      </div>

      <OptionGroup title="Title & intro" defaultOpen={false}>
        <div className="admin-grid-2">
          <label>
            Heading
            <input value={table.heading || ''} onChange={(e) => update({ heading: e.target.value })} placeholder="Sound" />
          </label>
          <label>
            Highlight
            <input value={table.highlight || ''} onChange={(e) => update({ highlight: e.target.value })} placeholder="FX" />
          </label>
        </div>
        <label>
          Intro
          <textarea value={table.intro || ''} onChange={(e) => update({ intro: e.target.value })} rows={2} />
        </label>
        <label className="admin-checkbox">
          <input type="checkbox" checked={!!table.separatorBefore} onChange={(e) => update({ separatorBefore: e.target.checked })} />
          Separator line before this table
        </label>
        {table.separatorBefore && (
          <label>
            Separator label
            <input value={table.separatorLabel || ''} onChange={(e) => update({ separatorLabel: e.target.value })} placeholder="// SOUND_FX" />
          </label>
        )}
      </OptionGroup>

      <OptionGroup title="Style" defaultOpen={false}>
        <div className="tpb-toggle-grid">
          <label className="admin-checkbox">
            <input type="checkbox" checked={table.collapsible !== false} onChange={(e) => update({ collapsible: e.target.checked })} />
            Collapsible panel
          </label>
          <label className="admin-checkbox">
            <input type="checkbox" checked={table.defaultOpen !== false} onChange={(e) => update({ defaultOpen: e.target.checked })} />
            Open by default
          </label>
          <label className="admin-checkbox">
            <input type="checkbox" checked={!!table.striped} onChange={(e) => update({ striped: e.target.checked })} />
            Striped rows
          </label>
          <label className="admin-checkbox">
            <input type="checkbox" checked={!!table.compact} onChange={(e) => update({ compact: e.target.checked })} />
            Compact rows
          </label>
          <label className="admin-checkbox">
            <input type="checkbox" checked={!!table.searchable} onChange={(e) => update({ searchable: e.target.checked })} />
            Search box for viewers
          </label>
          <label className="admin-checkbox">
            <input type="checkbox" checked={!!table.rowNumbers} onChange={(e) => update({ rowNumbers: e.target.checked })} />
            Row numbers
          </label>
        </div>
      </OptionGroup>

      <OptionGroup title={`Columns (${columns.length})`} defaultOpen={false}>
        <div className="tpb-column-list">
          {columns.map((col, cIdx) => (
            <SortableItem
              key={col.key || cIdx}
              className="tpb-column-row"
              listId={`table-cols-${tIdx}`}
              index={cIdx}
              onMove={(from, to) => update({ columns: reorder(columns, from, to) })}
            >
              <DragHandle listId={`table-cols-${tIdx}`} index={cIdx} />
              <input
                value={col.label || ''}
                onChange={(e) => updateColumn(cIdx, { label: e.target.value })}
                onBlur={() => {
                  if (/^col\d+$/.test(col.key) && col.label) updateColumn(cIdx, { key: uniqueKey(columns.filter((_, i) => i !== cIdx), slugifyColumnKey(col.label)) });
                }}
                title={`Data key: ${col.key}`}
              />
              <select value={col.style || 'text'} onChange={(e) => updateColumn(cIdx, { style: e.target.value })}>
                {COLUMN_STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select value={col.align || ''} onChange={(e) => updateColumn(cIdx, { align: e.target.value || undefined })}>
                {COLUMN_ALIGNS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
              <input value={col.width || ''} onChange={(e) => updateColumn(cIdx, { width: e.target.value || undefined })} placeholder="auto" title="e.g. 10rem or 25%" />
              <button type="button" className="admin-icon-btn danger" title="Remove column" disabled={columns.length <= 1} onClick={() => removeColumn(cIdx)}>
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </SortableItem>
          ))}
        </div>
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={addColumn}>
          <i className="fas fa-plus" aria-hidden="true" /> Add column
        </button>
      </OptionGroup>

      {!expanded && renderGrid()}
      {expanded && (
        <p className="admin-form-hint">Editing rows in the large editor…</p>
      )}
      <AdminModal
        open={expanded}
        onClose={() => setExpanded(false)}
        size="xl"
        title={`Rows · ${title}`}
        subtitle="Changes apply to the page straight away. Save the page when you're done."
        footer={<button type="button" className="btn btn-primary" onClick={() => setExpanded(false)}>Done</button>}
      >
        {renderGrid()}
      </AdminModal>
    </>
  );
}

export default function CustomTablesSectionFields({ section, onChange }) {
  const update = (patch) => onChange({ ...section, ...patch });
  const tables = section.tables || [];

  const setTable = (tIdx, next) => {
    const list = [...tables];
    list[tIdx] = next;
    update({ tables: list });
  };

  return (
    <div className="admin-form">
      <label>
        Section intro (optional)
        <textarea value={section.intro || ''} onChange={(e) => update({ intro: e.target.value })} rows={2} placeholder="Intro text above all tables…" />
      </label>

      {tables.map((table, tIdx) => (
        <SortableItem
          className="tpb-repeat-block stream-table-editor-block"
          key={tIdx}
          listId="custom-tables"
          index={tIdx}
          onMove={(from, to) => update({ tables: reorder(tables, from, to) })}
        >
          <TableEditor
            table={table}
            tIdx={tIdx}
            onChange={(next) => setTable(tIdx, next)}
            onDuplicate={() => update({ tables: [...tables.slice(0, tIdx + 1), JSON.parse(JSON.stringify(table)), ...tables.slice(tIdx + 1)] })}
            onRemove={() => {
              if (window.confirm('Remove this table and all its rows?')) update({ tables: tables.filter((_, i) => i !== tIdx) });
            }}
          />
        </SortableItem>
      ))}

      <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => update({ tables: [...tables, defaultTableBlock()] })}>
        <i className="fas fa-plus" aria-hidden="true" /> Add table
      </button>
    </div>
  );
}
